import { forwardRef, useEffect, useRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon } from '../icons/Icon';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  indeterminate?: boolean;
}

/** Checkbox — a custom box over a visually-hidden native input (keeps keyboard
 *  + form semantics). */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, indeterminate = false, className, disabled, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const node = innerRef.current;
    if (node) node.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label className={cx('tc-choice', disabled && 'tc-choice--disabled', className)}>
      <input
        ref={(node) => {
          innerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }}
        type="checkbox"
        className="tc-choice__native"
        disabled={disabled}
        {...rest}
      />
      <span className="tc-choice__box" aria-hidden>
        <Icon className="tc-choice__check" name={indeterminate ? 'minus' : 'check'} size={14} strokeWidth={3} />
      </span>
      {label != null && <span className="tc-choice__label">{label}</span>}
    </label>
  );
});
